import React, { useState, useRef, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Image as ImageIcon, Loader, AlertCircle, RefreshCw } from 'lucide-react';
import { getAbsoluteImageUrl, generateFallbackUrls, addCacheBuster } from '../utils/urlUtils';
import { retryImageOperation } from '../utils/networkResilience';
import { parseBackendError, ERROR_TYPES } from '../utils/errorHandling';
import { 
  startImageLoadTimer, 
  endImageLoadTimer, 
  recordOperationAttempt, 
  recordUserExperience, 
  recordImageError 
} from '../utils/performanceMonitor';

const LazyImage = ({
  src,
  alt = '',
  className = '',
  thumbnailSrc = null,
  width,
  height,
  onLoad,
  onError,
  placeholder = null,
  retryable = true,
  ...props
}) => {
  const [imageState, setImageState] = useState('loading'); // loading, loaded, error
  const [imageSrc, setImageSrc] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [fallbackUrls, setFallbackUrls] = useState([]);
  const [currentUrlIndex, setCurrentUrlIndex] = useState(0);
  const [showThumbnail, setShowThumbnail] = useState(!!thumbnailSrc);
  const [lastError, setLastError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const imgRef = useRef(null);
  const currentSrcRef = useRef(null);
  
  // Simplified intersection observer setup
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
    rootMargin: '50px'
  });

  // Combine refs safely
  const setRefs = (element) => {
    imgRef.current = element;
    if (inViewRef) {
      inViewRef(element);
    }
  };

  // Reset state when src changes
  useEffect(() => {
    if (src !== currentSrcRef.current) {
      setImageState('loading');
      setImageSrc(null);
      setRetryCount(0);
      setCurrentUrlIndex(0);
      setShowThumbnail(!!thumbnailSrc);
      setLastError(null);
      setErrorDetails(null);
      currentSrcRef.current = src;
      
      // Generate fallback URLs for the new src
      if (src) {
        const fallbacks = generateFallbackUrls(src);
        setFallbackUrls(fallbacks);
      } else {
        setFallbackUrls([]);
      }
    }
  }, [src, thumbnailSrc]);

  // Simplified image loading logic
  useEffect(() => {
    // Only load if in view (or intersection observer not supported) and we have a src
    const shouldLoad = (inView || typeof window === 'undefined' || !('IntersectionObserver' in window)) && 
                      src && 
                      imageState === 'loading';
                      
    if (shouldLoad) {
      loadImage();
    }
  }, [inView, src, imageState]);

  // Direct image loading function without complex caching
  const loadImage = async () => {
    if (!src) return;

    // Start performance monitoring for image load
    const loadTimerId = startImageLoadTimer(`lazy_load_${src}`, {
      operationType: 'load',
      imageUrl: src,
      isLazyLoad: true,
      retryCount: retryCount
    });

    try {
      // Get the primary URL to try
      const primaryUrl = getAbsoluteImageUrl(src);
      
      // Use network resilience for retry logic
      await retryImageOperation(async () => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          
          img.onload = () => {
            // Only update if this is still the current src
            if (src === currentSrcRef.current) {
              setImageSrc(primaryUrl);
              setImageState('loaded');
              setShowThumbnail(false); // Hide thumbnail when main image loads
              
              // Record successful load
              endImageLoadTimer(loadTimerId, { 
                success: true, 
                cached: false,
                imageUrl: primaryUrl,
                isLazyLoad: true
              });
              recordOperationAttempt('load', true);
              recordUserExperience({
                timeToFirstByte: performance.now(),
                timeToInteractive: performance.now()
              });
              
              if (onLoad) {
                onLoad({ target: img, src: primaryUrl });
              }
            }
            resolve();
          };
          
          img.onerror = () => {
            reject(new Error(`Failed to load image: ${primaryUrl}`));
          };
          
          img.src = primaryUrl;
        });
      }, {
        maxRetries: 1, // Let our fallback system handle additional retries
        operationName: 'lazy_image_load'
      });

    } catch (error) {
      // Record failed load operation
      endImageLoadTimer(loadTimerId, { 
        success: false, 
        error: error.message,
        imageUrl: src,
        isLazyLoad: true
      });
      recordOperationAttempt('load', false);
      recordImageError(error, { 
        operationType: 'load', 
        imageUrl: src,
        isLazyLoad: true,
        retryCount: retryCount
      });
      recordUserExperience({ isRetry: retryCount > 0 });
      
      // Parse and classify the error
      const parsedError = parseBackendError(error, {
        operation: 'image_load',
        src: src,
        retryCount: 0
      });
      
      setLastError(parsedError);
      setErrorDetails({
        originalUrl: src,
        primaryUrl: getAbsoluteImageUrl(src),
        timestamp: new Date().toISOString(),
        errorType: parsedError.type
      });
      
      // Try fallback URLs if primary fails
      await tryFallbackUrls(parsedError);
    }
  };

  // Enhanced fallback URL handling with comprehensive error reporting
  const tryFallbackUrls = async (originalError) => {
    if (fallbackUrls.length === 0 || currentUrlIndex >= fallbackUrls.length) {
      // No more fallbacks, show error with comprehensive details
      if (src === currentSrcRef.current) {
        setImageState('error');
        
        // Enhanced error reporting
        const finalError = {
          ...originalError,
          fallbacksAttempted: currentUrlIndex,
          totalFallbacks: fallbackUrls.length,
          allUrlsAttempted: [
            getAbsoluteImageUrl(src),
            ...fallbackUrls.slice(0, currentUrlIndex)
          ]
        };
        
        setLastError(finalError);
        setErrorDetails(prev => ({
          ...prev,
          fallbacksAttempted: currentUrlIndex,
          totalFallbacks: fallbackUrls.length,
          finalFailure: true
        }));
        
        if (onError) {
          onError({ 
            error: finalError, 
            src, 
            retryCount,
            errorDetails: errorDetails
          });
        }
      }
      return;
    }

    try {
      const fallbackUrl = fallbackUrls[currentUrlIndex];
      
      // Use network resilience for fallback attempts too
      await retryImageOperation(async () => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          
          img.onload = () => {
            if (src === currentSrcRef.current) {
              setImageSrc(fallbackUrl);
              setImageState('loaded');
              setShowThumbnail(false);
              
              // Clear error state on successful fallback
              setLastError(null);
              setErrorDetails(null);
              
              if (onLoad) {
                onLoad({ 
                  target: img, 
                  src: fallbackUrl, 
                  fallback: true,
                  fallbackIndex: currentUrlIndex,
                  originalSrc: src
                });
              }
            }
            resolve();
          };
          
          img.onerror = () => {
            reject(new Error(`Fallback URL failed: ${fallbackUrl}`));
          };
          
          img.src = fallbackUrl;
        });
      }, {
        maxRetries: 1,
        operationName: 'lazy_image_fallback'
      });

    } catch (fallbackError) {
      // Parse fallback error
      const parsedFallbackError = parseBackendError(fallbackError, {
        operation: 'image_fallback',
        src: fallbackUrls[currentUrlIndex],
        fallbackIndex: currentUrlIndex,
        retryCount: 0
      });
      
      // Update error details with fallback attempt info
      setErrorDetails(prev => ({
        ...prev,
        fallbackErrors: [
          ...(prev?.fallbackErrors || []),
          {
            url: fallbackUrls[currentUrlIndex],
            error: parsedFallbackError,
            index: currentUrlIndex
          }
        ]
      }));
      
      // Try next fallback
      setCurrentUrlIndex(prev => prev + 1);
      await tryFallbackUrls(originalError);
    }
  };

  // Enhanced retry function with exponential backoff
  const handleRetry = async () => {
    if (retryCount >= 3) return;
    
    const newRetryCount = retryCount + 1;
    setRetryCount(newRetryCount);
    setImageState('loading');
    setImageSrc(null);
    setCurrentUrlIndex(0);
    
    try {
      // Use network resilience for automatic retry with exponential backoff
      await retryImageOperation(async () => {
        // Add cache buster to primary URL for retry
        const retriedSrc = addCacheBuster(src, true);
        
        return new Promise((resolve, reject) => {
          const img = new Image();
          
          img.onload = () => {
            if (src === currentSrcRef.current) {
              setImageSrc(retriedSrc);
              setImageState('loaded');
              setShowThumbnail(false);
              
              if (onLoad) {
                onLoad({ target: img, src: retriedSrc, retry: true, retryCount: newRetryCount });
              }
            }
            resolve();
          };
          
          img.onerror = () => {
            reject(new Error(`Retry failed for image: ${retriedSrc}`));
          };
          
          img.src = retriedSrc;
        });
      }, {
        maxRetries: 2,
        baseDelay: 1000 * newRetryCount, // Exponential backoff: 1s, 2s, 3s
        operationName: 'lazy_image_retry'
      });

    } catch (error) {
      // If retry fails, try fallback URLs
      await tryFallbackUrls(error);
    }
  };

  const handleThumbnailLoad = () => {
    // Keep thumbnail visible until main image loads
  };

  const handleThumbnailError = () => {
    // Thumbnail failed to load, hide it
    setShowThumbnail(false);
  };

  // Render loading placeholder
  const renderPlaceholder = () => {
    if (placeholder) {
      return placeholder;
    }

    return (
      <div 
        className={`flex items-center justify-center bg-gray-100 ${className}`}
        style={{ width, height }}
      >
        <div className="flex flex-col items-center text-gray-400">
          {imageState === 'loading' ? (
            <>
              <Loader size={24} className="animate-spin mb-2" />
              <span className="text-xs">Loading...</span>
            </>
          ) : (
            <>
              <ImageIcon size={24} className="mb-2" />
              <span className="text-xs">Image</span>
            </>
          )}
        </div>
      </div>
    );
  };

  // Enhanced error state with better user feedback
  const renderError = () => {
    const getErrorMessage = () => {
      if (retryCount === 0) {
        return "Failed to load image";
      } else if (retryCount < 3) {
        return `Loading failed (${retryCount} ${retryCount === 1 ? 'retry' : 'retries'})`;
      } else {
        return "Image unavailable";
      }
    };

    const getErrorSubtext = () => {
      if (retryCount >= 3) {
        return "All retry attempts failed";
      } else if (fallbackUrls.length > 0 && currentUrlIndex >= fallbackUrls.length) {
        return "All fallback URLs failed";
      } else {
        return "Check your connection";
      }
    };

    return (
      <div 
        className={`flex flex-col items-center justify-center bg-gray-50 border border-gray-200 rounded ${className}`}
        style={{ width, height }}
      >
        <AlertCircle size={24} className="text-red-500 mb-2" />
        <span className="text-xs text-red-600 mb-1 text-center px-2 font-medium">
          {getErrorMessage()}
        </span>
        <span className="text-xs text-gray-500 mb-3 text-center px-2">
          {getErrorSubtext()}
        </span>
        
        {retryable && retryCount < 3 && (
          <button
            onClick={handleRetry}
            className="flex items-center space-x-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            disabled={imageState === 'loading'}
          >
            <RefreshCw size={12} className={imageState === 'loading' ? 'animate-spin' : ''} />
            <span>
              {imageState === 'loading' ? 'Retrying...' : `Retry (${retryCount + 1}/3)`}
            </span>
          </button>
        )}
        
        {retryCount >= 3 && (
          <div className="text-center">
            <span className="text-xs text-gray-500 block mb-1">Maximum retries reached</span>
            <span className="text-xs text-gray-400">Please refresh the page or try again later</span>
          </div>
        )}
      </div>
    );
  };

  // Don't render anything if no src
  if (!src) {
    return renderPlaceholder();
  }

  return (
    <div ref={setRefs} className="relative">
      {/* Thumbnail layer */}
      {showThumbnail && thumbnailSrc && (
        <img
          src={thumbnailSrc}
          alt={alt}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            imageState === 'loaded' ? 'opacity-0' : 'opacity-100'
          } ${className}`}
          onLoad={handleThumbnailLoad}
          onError={handleThumbnailError}
          style={{ width, height }}
        />
      )}

      {/* Main image */}
      {imageState === 'loaded' && imageSrc ? (
        <img
          ref={imgRef}
          src={imageSrc}
          alt={alt}
          className={`transition-opacity duration-300 opacity-100 ${className}`}
          width={width}
          height={height}
          {...props}
        />
      ) : imageState === 'error' ? (
        renderError()
      ) : (
        renderPlaceholder()
      )}

      {/* Loading overlay */}
      {imageState === 'loading' && !showThumbnail && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75">
          <Loader size={20} className="animate-spin text-gray-500" />
        </div>
      )}
    </div>
  );
};

export default LazyImage;